import type {
  FacilitiesSection,
  Filter,
  FilterDataMultiSelect,
  FilterDataOption,
} from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { createMultiSelectFilterSubset, getSelectedValues } from '@/app/common/filters/groups/filterGroupsUtils.ts';
import { ACCOMMODATION_FACILITY_FILTER_NAMES } from '@/app/common/filters/groups/facilityGroups.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';

const defaultAccommodationIds = [3, 9, 7, 8, 1, 2, 6, 11, 14, 4, 5, 10, 12, 13];

enum AnalyticsPropertyTypes {
  Covid19Measures = 'Covid-19_measures',
  SwimmingPool = 'Swimming_pool',
  FreeWifi = 'Free_Wifi',
  SpaWellness = 'Spa/Wellness',
  Children = 'Children',
  Accessibility = 'Accessibility',
  PetFriendly = 'Pet-friendly',
  Entertainment = 'Entertainment',
  AdultsOnly = 'Adults_only',
  Parking = 'Parking',
  Seafront = 'Seafront',
  Kitchen = 'Kitchen',
  EvCharge = 'EV_charge',
  Fitness = 'Fitness_facilities',
  RooftopTerrace = 'Rooftop_terrace',
  ChildrenPlayground = 'Children_playground',
  BabySitting = 'Baby_sitting',
  ChildrenClubs = 'Children_clubs',
  ChildrenMeal = 'Children_meal',
  ChildrenPool = 'Children_pool',
  Hiking = 'Hiking',
  Fishing = 'Fishing',
  Cycling = 'Cycling',
  BoardGames = 'Board_games',
  HorseRiding = 'Horse_riding',
  Golf = 'Golf',
  BicycleRental = 'Bicycle_rental',
  Canoe = 'Canoe',
  Diving = 'Diving',
  WindSurfing = 'Wind_surfing',
  WaterPark = 'Water_park',
  Snorkeling = 'Snorkeling',
  Bowling = 'Bowling',
  Billiards = 'Billiards',
  Scuba = 'Scuba',
  Massage = 'Massage',
  WaterSports = 'Water_sports',
  EveningEntertainment = 'Evening_entertainment',
  TennisCourt = 'Tennis_court',
  ArcadeRoom = 'Arcade_room',
  Kayaking = 'Kayaking',
  EcoTours = 'Eco_tours',
  Yoga = 'Yoga',
  NightClub = 'Night_club',
  Darts = 'Darts',
  IndoorPlayArea = 'Indoor_play_area',
  IndoorPool = 'Indoor_pool',
  RooftopPool = 'Rooftop_pool',
  Hammam = 'Hammam',
  Sauna = 'Sauna',
  SteamRoom = 'Steam_room',
  SaltPool = 'Salt_pool',
  InfinityPool = 'Infinity_pool',
  WaterSlide = 'Water_slide',
  SunTerrace = 'Sun_terrace',
  Picnic = 'Picnic',
  Bar = 'Bar',
  Pool_bar = 'Pool_bar',
  Restaurant = 'Restaurant',
  Bbq = 'Bbq',
  MiniMarket = 'Mini_market',
}

type FacilitiesFiltersNames =
  | FILTER_NAME.FACILITIES_GENERAL
  | FILTER_NAME.FACILITIES_FAMILY
  | FILTER_NAME.FACILITIES_ACTIVITY
  | FILTER_NAME.FACILITIES_WELLNESS
  | FILTER_NAME.FACILITIES_FOOD;

const facilitiesSections: {
  [key in FacilitiesFiltersNames]: FacilitiesSection;
} = {
  [FILTER_NAME.FACILITIES_GENERAL]: {
    labelKey: 'vacation.oss.filters.general.title',
    items: [
      {
        id: 0,
        name: 'freeWifi',
        translationKey: 'vacation.search.filters.facilities.options.freewifi',
        analyticsValue: AnalyticsPropertyTypes.FreeWifi,
      },
      {
        id: 1,
        name: 'petsAllowed',
        translationKey: 'vacation.search.filters.facilities.options.pets',
        analyticsValue: AnalyticsPropertyTypes.PetFriendly,
      },
      {
        id: 2,
        name: 'disableGuest',
        translationKey: 'vacation.search.filters.facilities.options.disableguest',
        analyticsValue: AnalyticsPropertyTypes.Accessibility,
      },
      {
        id: 3,
        name: 'parking',
        translationKey: 'vacation.search.filters.facilities.options.parking',
        analyticsValue: AnalyticsPropertyTypes.Parking,
      },
      {
        id: 4,
        name: 'pool',
        translationKey: 'vacation.search.filters.facilities.options.swimmingpool',
        analyticsValue: AnalyticsPropertyTypes.SwimmingPool,
      },
      {
        id: 5,
        name: 'children',
        translationKey: 'vacation.search.filters.facilities.options.children',
        analyticsValue: AnalyticsPropertyTypes.Children,
      },
      {
        id: 6,
        name: 'spa',
        translationKey: 'vacation.search.filters.facilities.options.spa',
        analyticsValue: AnalyticsPropertyTypes.SpaWellness,
      },
      {
        id: 7,
        name: 'adultsOnly',
        translationKey: 'vacation.search.filters.facilities.options.adultsonly',
        analyticsValue: AnalyticsPropertyTypes.AdultsOnly,
      },
      {
        id: 8,
        name: 'entertainment',
        translationKey: 'vacation.search.filters.facilities.options.entertainment',
        analyticsValue: AnalyticsPropertyTypes.Entertainment,
      },
      {
        id: 9,
        name: 'seafront',
        translationKey: 'vacation.search.filters.facilities.options.seafront',
        analyticsValue: AnalyticsPropertyTypes.Seafront,
      },
      {
        id: 11,
        name: 'kitchen',
        translationKey: 'vacation.search.filters.facilities.options.kitchen',
        analyticsValue: AnalyticsPropertyTypes.Kitchen,
      },
      {
        id: 12,
        name: 'generalEvCharge',
        translationKey: 'vacation.oss.filters.general.ev_charging',
        analyticsValue: AnalyticsPropertyTypes.EvCharge,
      },
      {
        id: 13,
        name: 'generalFitness',
        translationKey: 'vacation.oss.filters.general.fitness',
        analyticsValue: AnalyticsPropertyTypes.Fitness,
      },
      {
        id: 14,
        name: 'generalRooftopTerrace',
        translationKey: 'vacation.oss.filters.rooftop_terrace',
        analyticsValue: AnalyticsPropertyTypes.RooftopTerrace,
      },
    ],
  },
  [FILTER_NAME.FACILITIES_FAMILY]: {
    labelKey: 'vacation.oss.filters.family.title',
    items: [
      {
        id: 15,
        name: 'familyChildrenPlayground',
        translationKey: 'vacation.oss.filters.family.playground',
        analyticsValue: AnalyticsPropertyTypes.ChildrenPlayground,
      },
      {
        id: 16,
        name: 'familyBabySitting',
        translationKey: 'vacation.oss.filters.family.baby_sitting',
        analyticsValue: AnalyticsPropertyTypes.BabySitting,
      },
      {
        id: 17,
        name: 'familyChildrenClubs',
        translationKey: 'vacation.oss.filters.family.club',
        analyticsValue: AnalyticsPropertyTypes.ChildrenClubs,
      },
      {
        id: 18,
        name: 'familyChildrenMeal',
        translationKey: 'vacation.oss.filters.family.meal',
        analyticsValue: AnalyticsPropertyTypes.ChildrenMeal,
      },
      {
        id: 19,
        name: 'familyChildrenPool',
        translationKey: 'vacation.oss.filters.family.pool',
        analyticsValue: AnalyticsPropertyTypes.ChildrenPool,
      },
    ],
  },
  [FILTER_NAME.FACILITIES_ACTIVITY]: {
    labelKey: 'vacation.oss.filters.activities.title',
    items: [
      {
        id: 20,
        name: 'activitiesHiking',
        translationKey: 'vacation.oss.filters.activities.hiking',
        analyticsValue: AnalyticsPropertyTypes.Hiking,
      },
      {
        id: 21,
        name: 'activitiesFishing',
        translationKey: 'vacation.oss.filters.activities.fishing',
        analyticsValue: AnalyticsPropertyTypes.Fishing,
      },
      {
        id: 22,
        name: 'activitiesCycling',
        translationKey: 'vacation.oss.filters.activities.cycling',
        analyticsValue: AnalyticsPropertyTypes.Cycling,
      },
      {
        id: 23,
        name: 'activitiesBoardGames',
        translationKey: 'vacation.oss.filters.activities.board_games',
        analyticsValue: AnalyticsPropertyTypes.BoardGames,
      },
      {
        id: 24,
        name: 'activitiesHorseRiding',
        translationKey: 'vacation.oss.filters.activities.horse_riding',
        analyticsValue: AnalyticsPropertyTypes.HorseRiding,
      },
      {
        id: 25,
        name: 'activitiesGolf',
        translationKey: 'vacation.oss.filters.activities.golf',
        analyticsValue: AnalyticsPropertyTypes.Golf,
      },
      {
        id: 26,
        name: 'activitiesBicycleRental',
        translationKey: 'vacation.oss.filters.activities.bike_rental',
        analyticsValue: AnalyticsPropertyTypes.BicycleRental,
      },
      {
        id: 27,
        name: 'activitiesCanoeing',
        translationKey: 'vacation.oss.filters.activities.canoe',
        analyticsValue: AnalyticsPropertyTypes.Canoe,
      },
      {
        id: 28,
        name: 'activitiesDiving',
        translationKey: 'vacation.oss.filters.activities.diving',
        analyticsValue: AnalyticsPropertyTypes.Diving,
      },
      {
        id: 29,
        name: 'activitiesWindsurfing',
        translationKey: 'vacation.oss.filters.activities.windsurfing',
        analyticsValue: AnalyticsPropertyTypes.WindSurfing,
      },
      {
        id: 30,
        name: 'activitiesWaterPark',
        translationKey: 'vacation.oss.filters.activities.water_park',
        analyticsValue: AnalyticsPropertyTypes.WaterPark,
      },
      {
        id: 31,
        name: 'activitiesSnorkelling',
        translationKey: 'vacation.oss.filters.activities.snorkelling',
        analyticsValue: AnalyticsPropertyTypes.Snorkeling,
      },
      {
        id: 32,
        name: 'activitiesBowling',
        translationKey: 'vacation.oss.filters.activities.bowling',
        analyticsValue: AnalyticsPropertyTypes.Bowling,
      },
      {
        id: 33,
        name: 'activitiesBilliards',
        translationKey: 'vacation.oss.filters.activities.billiards',
        analyticsValue: AnalyticsPropertyTypes.Billiards,
      },
      {
        id: 34,
        name: 'activitiesScuba',
        translationKey: 'vacation.oss.filters.activities.scuba',
        analyticsValue: AnalyticsPropertyTypes.Scuba,
      },
      {
        id: 35,
        name: 'activitiesMassage',
        translationKey: 'vacation.oss.filters.activities.massage',
        analyticsValue: AnalyticsPropertyTypes.Massage,
      },
      {
        id: 36,
        name: 'activitiesWaterSport',
        translationKey: 'vacation.oss.filters.activities.water_sports',
        analyticsValue: AnalyticsPropertyTypes.WaterSports,
      },
      {
        id: 37,
        name: 'activitiesEveningEntertainment',
        translationKey: 'vacation.oss.filters.activities.entertainment',
        analyticsValue: AnalyticsPropertyTypes.EveningEntertainment,
      },
      {
        id: 38,
        name: 'activitiesTennisCourt',
        translationKey: 'vacation.oss.filters.activities.tennis',
        analyticsValue: AnalyticsPropertyTypes.TennisCourt,
      },
      {
        id: 39,
        name: 'activitiesArcadeRoom',
        translationKey: 'vacation.oss.filters.activities.arcade',
        analyticsValue: AnalyticsPropertyTypes.ArcadeRoom,
      },
      {
        id: 40,
        name: 'activitiesKayaking',
        translationKey: 'vacation.oss.filters.activities.kayak',
        analyticsValue: AnalyticsPropertyTypes.Kayaking,
      },
      {
        id: 41,
        name: 'activitiesEcotours',
        translationKey: 'vacation.oss.filters.activities.ecotour',
        analyticsValue: AnalyticsPropertyTypes.EcoTours,
      },
      {
        id: 42,
        name: 'activitiesYoga',
        translationKey: 'vacation.oss.filters.activities.yoga',
        analyticsValue: AnalyticsPropertyTypes.Yoga,
      },
      {
        id: 43,
        name: 'activitiesNightclub',
        translationKey: 'vacation.oss.filters.activities.nightclub',
        analyticsValue: AnalyticsPropertyTypes.NightClub,
      },
      {
        id: 44,
        name: 'activitiesDarts',
        translationKey: 'vacation.oss.filters.activities.darts',
        analyticsValue: AnalyticsPropertyTypes.Darts,
      },
      {
        id: 45,
        name: 'activitiesPlayArea',
        translationKey: 'vacation.oss.filters.activities.indoor_games',
        analyticsValue: AnalyticsPropertyTypes.IndoorPlayArea,
      },
    ],
  },
  [FILTER_NAME.FACILITIES_WELLNESS]: {
    labelKey: 'vacation.oss.filters.wellness.title',
    items: [
      {
        id: 46,
        name: 'spaIndoorPool',
        translationKey: 'vacation.oss.filters.wellness.indoor_pool',
        analyticsValue: AnalyticsPropertyTypes.IndoorPool,
      },
      {
        id: 47,
        name: 'spaRooftopPool',
        translationKey: 'vacation.oss.filters.wellness.rooftop_pool',
        analyticsValue: AnalyticsPropertyTypes.RooftopPool,
      },
      {
        id: 48,
        name: 'spaHammam',
        translationKey: 'vacation.oss.filters.wellness.hammam',
        analyticsValue: AnalyticsPropertyTypes.Hammam,
      },
      {
        id: 49,
        name: 'spaSauna',
        translationKey: 'vacation.oss.filters.wellness.sauna',
        analyticsValue: AnalyticsPropertyTypes.Sauna,
      },
      {
        id: 50,
        name: 'spaSteamRoom',
        translationKey: 'vacation.oss.filters.wellness.steam_room',
        analyticsValue: AnalyticsPropertyTypes.SteamRoom,
      },
      {
        id: 51,
        name: 'spaSaltPool',
        translationKey: 'vacation.oss.filters.wellness.salt_pool',
        analyticsValue: AnalyticsPropertyTypes.SaltPool,
      },
      {
        id: 52,
        name: 'spaInfinityPool',
        translationKey: 'vacation.oss.filters.wellness.infinity_pool',
        analyticsValue: AnalyticsPropertyTypes.InfinityPool,
      },
      {
        id: 53,
        name: 'spaWaterSlide',
        translationKey: 'vacation.oss.filters.wellness.waterslide',
        analyticsValue: AnalyticsPropertyTypes.WaterSlide,
      },
    ],
  },
  [FILTER_NAME.FACILITIES_FOOD]: {
    labelKey: 'vacation.oss.filters.food.title',
    items: [
      {
        id: 54,
        name: 'foodSunTerrace',
        translationKey: 'vacation.oss.filters.food.sun_terrace',
        analyticsValue: AnalyticsPropertyTypes.SunTerrace,
      },
      {
        id: 55,
        name: 'foodPicnic',
        translationKey: 'vacation.oss.filters.food.picnic',
        analyticsValue: AnalyticsPropertyTypes.Picnic,
      },
      {
        id: 56,
        name: 'foodBar',
        translationKey: 'vacation.oss.filters.food.bar',
        analyticsValue: AnalyticsPropertyTypes.Bar,
      },
      {
        id: 57,
        name: 'foodPoolBar',
        translationKey: 'vacation.oss.filters.food.poolbar',
        analyticsValue: AnalyticsPropertyTypes.Pool_bar,
      },
      {
        id: 58,
        name: 'foodRestaurant',
        translationKey: 'vacation.oss.filters.food.restaurant',
        analyticsValue: AnalyticsPropertyTypes.Restaurant,
      },
      {
        id: 59,
        name: 'foodBbq',
        translationKey: 'vacation.oss.filters.food.bbq',
        analyticsValue: AnalyticsPropertyTypes.Bbq,
      },
      {
        id: 60,
        name: 'foodMinimarket',
        translationKey: 'vacation.oss.filters.food.minimarket',
        analyticsValue: AnalyticsPropertyTypes.MiniMarket,
      },
    ],
  },
};

type CreateDefaultFacilitiesFiltersProps = {
  searchParams: SearchParams;
  translationDictionary: TranslationDictionary;
};
const isFamilySearchFrom = (searchParams: SearchParams): boolean => !!searchParams?.[URLParameters.AGES];

const hasSelectedOption = (filter: Filter | undefined): boolean =>
  !!filter && ((filter.data as FilterDataMultiSelect).options ?? []).some((option) => option.selected);

const keepSection = (name: FacilitiesFiltersNames, filter: Filter | undefined, isFamilySearch: boolean): boolean =>
  name !== FILTER_NAME.FACILITIES_FAMILY || isFamilySearch || hasSelectedOption(filter);

const createDefaultFacilitiesFilters = ({
  searchParams,
  translationDictionary,
}: CreateDefaultFacilitiesFiltersProps): Filter[] => {
  const sectionNames = ACCOMMODATION_FACILITY_FILTER_NAMES as FacilitiesFiltersNames[];
  const isFamilySearch = isFamilySearchFrom(searchParams);

  return sectionNames
    .map((name) => {
      const { labelKey, items } = facilitiesSections[name];
      const selectedValues = getSelectedValues({ name, searchParams });
      const defaultIds = name === FILTER_NAME.FACILITIES_GENERAL ? defaultAccommodationIds : [];
      const selectedIds = items.map((item) => item.id).filter((id) => selectedValues.includes(`${id}`));
      const ids = [...defaultIds, ...selectedIds.filter((id) => !defaultIds.includes(id))];

      const options = ids
        .map((id) => {
          const item = items.find((candidate) => candidate.id === id);
          return item
            ? ({
                label: getTranslationServer(item.translationKey, translationDictionary),
                selected: selectedValues.includes(`${id}`),
                value: `${id}`,
                counter: undefined,
              } as FilterDataOption)
            : undefined;
        })
        .filter((option) => !!option);

      const filter = options.length
        ? ({
            name,
            type: FILTER_TYPE.MULTI_SELECT,
            data: {
              label: getTranslationServer(labelKey, translationDictionary),
              options,
            } as FilterDataMultiSelect,
          } as Filter)
        : undefined;

      return keepSection(name, filter, isFamilySearch) ? filter : undefined;
    })
    .filter((f) => !!f);
};

type CreateFacilitiesFilters = {
  searchParams: SearchParams;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};
export const createFacilitiesFilters = ({
  searchParams,
  dynamicFiltersResponse,
  translationDictionary,
}: CreateFacilitiesFilters): Filter[] => {
  if (!dynamicFiltersResponse?.accommodationFacilities) {
    return createDefaultFacilitiesFilters({ searchParams, translationDictionary });
  }

  const filterCreator = (name: FacilitiesFiltersNames) =>
    createMultiSelectFilterSubset({
      name,
      filterSectionItems: facilitiesSections[name].items,
      searchParams,
      dynamicFilter: dynamicFiltersResponse?.accommodationFacilities,
      labelKey: facilitiesSections[name].labelKey,
      translationDictionary,
    });

  const facilitiesGeneralFilter = filterCreator(FILTER_NAME.FACILITIES_GENERAL);
  const facilitiesFamilyFilter = filterCreator(FILTER_NAME.FACILITIES_FAMILY);
  const facilitiesActivityFilter = filterCreator(FILTER_NAME.FACILITIES_ACTIVITY);
  const facilitiesWellnessFilter = filterCreator(FILTER_NAME.FACILITIES_WELLNESS);
  const facilitiesFoodFilter = filterCreator(FILTER_NAME.FACILITIES_FOOD);
  const isFamilySearch = isFamilySearchFrom(searchParams);

  return [
    ...[facilitiesGeneralFilter],
    ...(keepSection(FILTER_NAME.FACILITIES_FAMILY, facilitiesFamilyFilter, isFamilySearch)
      ? [facilitiesFamilyFilter]
      : []),
    ...[facilitiesActivityFilter],
    ...[facilitiesWellnessFilter],
    ...[facilitiesFoodFilter],
  ].filter((f) => !!f);
};
